import {
    collection,
    addDoc,
    doc,
    writeBatch,
    query,
    where,
    getDocs,
    orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Create a new job
 * Writes to both /jobs and /employers/{employerId}/jobs
 * @param {Object} jobData - Job data object
 * @param {string} employerId - Employer's UID
 * @returns {Promise<string>} - The ID of the created job
 */
export const createJob = async (jobData, employerId) => {
    try {
        const batch = writeBatch(db);

        // 1. Create a ref for the global jobs collection to get an ID
        const globalJobRef = doc(collection(db, 'jobs'));
        const jobId = globalJobRef.id;

        // 2. Prepare the job document with timestamps and ID
        const jobDocument = {
            ...jobData,

            employerId,
            lastActionBy: {
                userId: employerId,
                role: 'employer'
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };

        // 3. Set data in global jobs collection
        batch.set(globalJobRef, jobDocument);

        // 4. Create child Shift documents for each date
        if (Array.isArray(jobData.dates) && jobData.dates.length > 0) {
            jobData.dates.forEach((dateString) => {
                const shiftRef = doc(collection(globalJobRef, 'shifts'), dateString);
                const shiftDocument = {
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    date: dateString,
                    vacancies: Number(jobData.vacancies) || 1,
                    budget: {
                        value: Number(jobData.budget?.value) || 0,
                        currency: jobData.budget?.currency || 'INR',
                    },
                    status: 'pending',
                    assignedProfessionalIds: [],
                    assignedProfessionalsDetails: [],
                    completedProfessionalIds: [],
                    applicationsList: []
                };
                batch.set(shiftRef, shiftDocument);
            });
        }

        // 5. Commit the batch
        await batch.commit();

        return jobId;
    } catch (error) {
        console.error('Error creating job:', error);
        throw error;
    }
};

/**
 * Get jobs for a specific employer
 * @param {string} employerId 
 * @returns {Promise<Array>} List of jobs
 */
export const getEmployerJobs = async (employerId) => {
    try {
        const jobsRef = collection(db, 'jobs');
        const q = query(jobsRef, where('employerId', '==', employerId), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error fetching employer jobs:', error);
        throw error;
    }
};
/**
 * Cancel a job
 * @param {string} jobId 
 * @returns {Promise<Object>} Result
 */
export const cancelJob = async (jobId) => {
    try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../config/firebase');
        const cancelJobFn = httpsCallable(functions, 'cancelJob');
        const result = await cancelJobFn({ jobId });
        return result.data;
    } catch (error) {
        console.error('Error cancelling job:', error);
        throw error;
    }
};
